import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Conditional } from '@resurs/react-utils'
import {
  Button,
  Column,
  Columns,
  Container,
  RadioButtonGroup,
} from '@resurs/midas-react'
import { colors, midas } from '@resurs/midas'
import { useLocaleState } from '@merchant-portal/merchant-portal-base'
import Page from '../../../../internal/Page'
import { getText } from '../../../../internal/TextLabels'
import { format } from '../../../../models/format'
import { CustomerTypeModel } from '../../models/private-models'
import GovernmentIdInput from './GovernmentIdInput'
import OrganisationIdInput from './OrganisationIdInput'
import PaymentPerMonth from '../shared/PaymentPerMonth'
import {
  validateAge,
  validateNaturalGovernmentId,
} from '../../../../util/governmentIdUtils'
import PaymentMethodNotAvailable from '../PaymentMethodsNotAvailable'
import { useOnUpdate, useToggle } from '../../../../util/utility-hooks'

const testRegex = (countryCode, governmentId) =>
  new RegExp(format.governmentId[countryCode].regex).test(governmentId)

const GovernmentIdPage = props => {
  const getCustomerTypeFromProps = () =>
    (props.data.wizard.customerTypes &&
      props.data.wizard.customerTypes.length > 1 &&
      CustomerTypeModel.NATURAL) ||
    (props.data.wizard.customerTypes &&
      props.data.wizard.customerTypes.length === 1 &&
      props.data.wizard.customerTypes[0]) ||
    CustomerTypeModel.NATURAL

  const { country, language } = useLocaleState()
  const [governmentId, setGovernmentId] = useState('')
  const [validOrgId, setValidOrgId] = useState(false)
  const [customerType, setCustomerType] = useState(getCustomerTypeFromProps())

  useEffect(() => {
    setCustomerType(getCustomerTypeFromProps())
  }, [props.data.wizard.customerTypes])

  const onChangeRadioButton = value => {
    setCustomerType(value)
  }

  const buttonDisabled = () => {
    if (customerType === CustomerTypeModel.NATURAL) {
      return !(
        governmentId &&
        testRegex(country, governmentId) &&
        validateAge(country, governmentId) &&
        validateNaturalGovernmentId(country, governmentId)
      )
    }
    return !validOrgId
  }

  const handleNext = e => {
    e.preventDefault()
    props.onNext({
      governmentId,
      validOrgId,
      customerType,
      analyticsData: {
        dimension7: customerType,
      },
    })
  }

  const isCustomerTypesAvailableForMerchantPortal = () => {
    return props.data.wizard.customerTypes.length > 0
  }

  const [arePaymentMethodsAvailable, togglePaymentMethodsAvailable] =
    useToggle(true)

  useOnUpdate(() => {
    togglePaymentMethodsAvailable(
      !(
        !isCustomerTypesAvailableForMerchantPortal() ||
        (props.data.current.governmentId !== null &&
          !props.data.wizard.newPageLoading &&
          !(props.data.paymentMethods && props.data.paymentMethods.length))
      ),
    )
  }, [
    props.data.current.governmentId,
    props.data.wizard,
    props.data.paymentMethods,
  ])

  let values = []
  if (
    props.data.wizard.customerTypes &&
    props.data.wizard.customerTypes.length > 0
  ) {
    values = props.data.wizard.customerTypes.map(item => ({
      key: item,
      value: item,
      valueLabel: getText(`payApply.applicant.${item}`),
    }))
  }

  return (
    <div>
      <Conditional when={!arePaymentMethodsAvailable}>
        <PaymentMethodNotAvailable />
      </Conditional>
      <Container>
        <Columns>
          <Column col1 colMd0 />
          <Column col10 colMd12>
            <form onSubmit={e => handleNext(e)}>
              <Page title={getText('payApply.applicant.newApplication')}>
                <Container>
                  <div
                    style={{
                      padding: '1.5rem',
                    }}
                  >
                    {values.length > 1 && (
                      <RadioButtonGroup
                        id="gov-org-radio"
                        name="gov-org-radio"
                        values={values}
                        defaultCheckedValue={customerType}
                        onChange={e => onChangeRadioButton(e.target.value)}
                        styling={{ color: colors.primaryMain }}
                        inline
                      />
                    )}
                    {values.length === 1 && (
                      <label
                        style={{ paddingTop: '1.5rem' }}
                        className={midas.formLabel}
                      >
                        {getText(`payApply.applicant.label.${customerType}`)}
                      </label>
                    )}
                    <div style={{ display: 'flex' }}>
                      <div style={{ flex: '1 1 50%' }}>
                        {customerType === CustomerTypeModel.NATURAL && (
                          <GovernmentIdInput
                            countryCode={country}
                            language={language}
                            governmentId={governmentId}
                            updateGovernmentId={value => setGovernmentId(value)}
                            disabled={
                              props.data.wizard.newPageLoading ||
                              !isCustomerTypesAvailableForMerchantPortal()
                            }
                          />
                        )}
                        {customerType === CustomerTypeModel.LEGAL && (
                          <OrganisationIdInput
                            countryCode={country}
                            orgId={governmentId}
                            updateOrgId={id => setGovernmentId(id)}
                            disabled={props.data.wizard.newPageLoading}
                            orgIdValid={validation =>
                              setValidOrgId(validation.valid)
                            }
                          />
                        )}
                      </div>
                      <div
                        style={{
                          color: colors.primaryMain,
                          display: 'flex',
                          justifyContent: 'flex-end',
                          padding: '1.8rem',
                          paddingRight: '1rem',
                        }}
                      >
                        <i
                          className={[midas.icon, midas.midasArrow31].join(' ')}
                        />
                      </div>
                      <Column col4 colMd3>
                        <div style={{ display: 'flex', marginTop: '-0.45rem' }}>
                          <div>
                            <strong>
                              {customerType === CustomerTypeModel.NATURAL
                                ? getText('payApply.applicant.start')
                                : getText('payApply.applicant.legal.start')}
                            </strong>
                          </div>
                        </div>
                      </Column>
                    </div>
                  </div>
                  <div
                    style={{
                      padding: '2rem 2rem 2rem 1rem',
                    }}
                  >
                    <PaymentPerMonth
                      customerType={customerType}
                      disabled={!isCustomerTypesAvailableForMerchantPortal()}
                      paymentMethodsExist={props.paymentMethodsExist}
                    />
                  </div>
                  <Column col1 colMd0 />
                </Container>
              </Page>
              <Page>
                <div
                  style={{
                    padding: '2rem',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    id="gov-id-button-next"
                    type="primary"
                    form
                    loading={props.data.wizard.newPageLoading}
                    disabled={
                      buttonDisabled() ||
                      !isCustomerTypesAvailableForMerchantPortal()
                    }
                  >
                    {getText('payApply.applicant.search')}
                  </Button>
                </div>
              </Page>
            </form>
          </Column>
        </Columns>
      </Container>
    </div>
  )
}

GovernmentIdPage.propTypes = {
  data: PropTypes.object,
  paymentMethodsExist: PropTypes.bool,
  onNext: PropTypes.func,
}

GovernmentIdPage.defaultProps = {
  onNext: null,
  data: null,
  paymentMethodsExist: true,
}

export default GovernmentIdPage
